import memoize from 'lodash/memoize';
import { isUndefined } from '../../../../predicates/type';
import {
	BaseNormalizerAtomProps,
	FloatAtomFn,
	FloatNormalizerAtomProps,
	NormalizerValueType,
	NumberAtomFn,
	NumberNormalizerAtomProps,
	PrimitiveValidatorsFn,
} from '../../types';
import { combineNormalizerAtoms } from '../base';
import {
	allowDigitsOnlyTransformer,
	commaCountAtom,
	commaCountValidator,
	commaToNumberFormatTransformer,
	decimalFulfilledValueIndicator,
	forbidNaNValidator,
	forbidWrongRangeValidator,
	isIntAtom,
	minMaxAtom,
	numericFulfilledValueIndicator,
	positiveValueValidator,
	skipNonNumericSymbolsTransformer,
	toNumberAtom,
	toPinCodeAtom,
} from '../numberAtoms';
import { MAX_INT32_VALUE } from '../../../../utils/formValidators';

function normalizeNumberOptions(options?: NumberNormalizerAtomProps) {
	const newOptions: NumberNormalizerAtomProps = {};

	if (isUndefined(options?.min)) {
		newOptions.min = -MAX_INT32_VALUE;
	}

	if (isUndefined(options?.max)) {
		newOptions.max = MAX_INT32_VALUE;
	}

	return newOptions;
}

export const intNormalizer = memoize((options?: NumberNormalizerAtomProps) => {
	return (value: NormalizerValueType, prevValue?: NormalizerValueType) => {
		options = {
			...options,
			...normalizeNumberOptions(options),
		};
		const primitiveValidators: Array<PrimitiveValidatorsFn> = [forbidNaNValidator];
		const atoms: Array<NumberAtomFn> = [];

		if (!options?.allowNegativeValue) {
			primitiveValidators.push(positiveValueValidator);
		}

		atoms.push(toNumberAtom);
		atoms.push(isIntAtom);
		atoms.push(minMaxAtom);

		return combineNormalizerAtoms({
			atoms,
			value,
			prevValue,
			fulfilledValueIndicator: numericFulfilledValueIndicator,
			options,
			primitiveValidators,
			transformers: [skipNonNumericSymbolsTransformer],
		});
	};
});

export const floatNormalizer = memoize((options?: FloatNormalizerAtomProps) => {
	return (value: NormalizerValueType, prevValue?: NormalizerValueType) => {
		options = {
			...options,
			...normalizeNumberOptions(options),
		};

		const primitiveValidators: Array<PrimitiveValidatorsFn> = [
			forbidNaNValidator,
			commaCountValidator,
			forbidWrongRangeValidator,
		];
		const atoms: Array<FloatAtomFn> = [];

		if (isUndefined(options?.digitsAfterDot)) {
			options.digitsAfterDot = 6;
		}

		if (!options?.allowNegativeValue) {
			primitiveValidators.push(positiveValueValidator);
		}

		atoms.push(commaCountAtom);
		atoms.push(toNumberAtom);
		atoms.push(minMaxAtom);

		return combineNormalizerAtoms({
			atoms,
			value,
			prevValue,
			options,
			fulfilledValueIndicator: decimalFulfilledValueIndicator,
			primitiveValidators,
			transformers: [skipNonNumericSymbolsTransformer, commaToNumberFormatTransformer],
		});
	};
});

export const pinCodeNormalizer = memoize((options?: BaseNormalizerAtomProps) => {
	return (value: NormalizerValueType, prevValue?: NormalizerValueType) => {
		return combineNormalizerAtoms({
			atoms: [toPinCodeAtom],
			value,
			prevValue,
			options,
			transformers: [allowDigitsOnlyTransformer],
		});
	};
});
